import { useCallback, useEffect, useRef } from 'react';

const STOP_TYPING_MS = 400;

/** Split pasted / multi-format voucher lists into space-separated tokens. */
export function normalizeScannerPaste(text) {
  return String(text || '')
    .split(/[\s.,;|/\\]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ');
}

export function withTrailingSpace(value) {
  const current = value || '';
  if (!current.trim()) return current;
  return current.endsWith(' ') ? current : `${current} `;
}

/**
 * Scanner-mode voucher field helpers:
 * - after each scan / typing pause → trailing space
 * - Enter/Tab → trailing space
 * - paste → normalize separators + trailing space
 */
export function useScannerVoucherHandlers(isScanner, setFormData) {
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (!isScanner) clearTimeout(timerRef.current);
  }, [isScanner]);

  const scheduleTrailingSpace = useCallback(() => {
    clearTimeout(timerRef.current);
    if (!isScanner) return;

    timerRef.current = setTimeout(() => {
      setFormData((prev) => {
        if (!prev.IsScanner) return prev;
        const current = prev.VoucherNo || '';
        if (!current.trim() || current.endsWith(' ')) return prev;
        return { ...prev, VoucherNo: `${current} ` };
      });
    }, STOP_TYPING_MS);
  }, [isScanner, setFormData]);

  const onChange = useCallback(
    (e) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, VoucherNo: value }));
      scheduleTrailingSpace();
    },
    [setFormData, scheduleTrailingSpace]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (!isScanner) return;
      if (e.key !== 'Enter' && e.key !== 'Tab') return;

      e.preventDefault();
      clearTimeout(timerRef.current);
      setFormData((prev) => ({
        ...prev,
        VoucherNo: withTrailingSpace(prev.VoucherNo),
      }));
    },
    [isScanner, setFormData]
  );

  const onPaste = useCallback(
    (e) => {
      if (!isScanner) return;

      e.preventDefault();
      clearTimeout(timerRef.current);

      const pasted = normalizeScannerPaste(e.clipboardData.getData('text'));
      if (!pasted) return;

      const input = e.target;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;

      setFormData((prev) => {
        const current = prev.VoucherNo || '';
        const before = current.slice(0, start);
        const after = current.slice(end);

        let next = before;
        if (before && !/\s$/.test(before)) next += ' ';
        next += pasted;
        next += ' ';

        const rest = after.replace(/^\s+/, '');
        if (rest) next += rest;

        next = next.replace(/\s+/g, ' ');
        if (next.trim() && !next.endsWith(' ')) next += ' ';

        return { ...prev, VoucherNo: next };
      });
    },
    [isScanner, setFormData]
  );

  return { onChange, onKeyDown, onPaste };
}
